/* Homepage-only Shopify runtime. Purchases and form submission remain native. */
(() => {
  'use strict';
  const main = document.getElementById('main');
  if (!main || !main.querySelector('.hero-overlay')) return;
  const config = window.AC20 || {};
  const homeHTML = main.innerHTML;
  const homeTitle = document.title;
  const mobileFooter = matchMedia('(max-width: 767px)');
  const dialog = document.getElementById('media-dialog');
  const lightbox = document.getElementById('lightbox-content');
  let currentView = 'home';
  let videoTrigger = null;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));

  function syncFooter() {
    document.querySelectorAll('#footer .ac-footer-group').forEach(group => {
      group.open = !mobileFooter.matches;
      const summary = group.querySelector('summary');
      if (!summary) return;
      if (mobileFooter.matches) summary.removeAttribute('tabindex');
      else summary.tabIndex = -1;
    });
  }

  function focusSection(id) {
    const target = id ? document.getElementById(id) : null;
    if (target && main.contains(target)) {
      target.setAttribute('tabindex', '-1');
      target.scrollIntoView({ block: 'start', behavior: 'instant' });
      target.focus({ preventScroll: true });
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
    }
  }

  function policyHTML(policy) {
    const content = document.createElement('div');
    // This HTML is compiled from the same reviewed local policy source as the theme.
    content.innerHTML = policy.contentHtml || `<p>${escape(policy.text || '')}</p>`;
    content.querySelectorAll('.policy-text').forEach(element => element.classList.remove('policy-text'));
    content.querySelectorAll('table').forEach(table => {
      table.classList.add('policy-data-table');
      const labels = [...table.querySelectorAll('thead th')].map(cell => cell.textContent.trim());
      table.querySelectorAll('tbody td').forEach(cell => { cell.dataset.label = labels[cell.cellIndex] || ''; });
    });
    const headings = [...content.querySelectorAll('h2')];
    headings.forEach((heading, index) => {
      if (!heading.id) heading.id = `policy-section-${index}`;
      heading.tabIndex = -1;
    });
    const contents = headings.map(heading => `<a href="#/policies/${encodeURIComponent(policy.slug)}?section=${encodeURIComponent(heading.id)}">${escape(heading.textContent)}</a>`).join('');
    return `<section class="page-title wrap service-title"><p class="eyebrow">ACTICLEAR CUSTOMER CARE</p><h1>${escape(policy.title)}</h1>${policy.lead ? `<p>${escape(policy.lead)}</p>` : ''}</section><div class="service-layout wrap"><aside class="service-toc"><p class="eyebrow">ON THIS PAGE</p><nav aria-label="Policy contents">${contents}</nav><a class="text-link" href="#/">Back to home</a></aside><article class="policy-text policy-rich">${content.innerHTML}<div class="service-next"><a class="outline" href="#/">Back to home</a></div></article></div>`;
  }

  function restoreHome() {
    const restored = document.createElement('div');
    restored.innerHTML = homeHTML;
    const reviews = restored.querySelector('#reviews');
    if (reviews && window.ACTICLEAR_REVIEW_CAROUSEL?.render) {
      reviews.outerHTML = window.ACTICLEAR_REVIEW_CAROUSEL.render();
    }
    main.innerHTML = restored.innerHTML;
    document.title = homeTitle;
    document.body.dataset.route = 'home';
    currentView = 'home';
  }

  function legacyDestination(path, query) {
    const routes = config.legacyRoutes || {};
    let destination = Object.prototype.hasOwnProperty.call(routes, path) ? routes[path] : null;
    if (!destination && (path === '/product' || path.startsWith('/product/'))) destination = '/collections/all';
    if (!destination) return null;
    const url = new URL(destination, location.href);
    const incoming = new URLSearchParams(query);
    if (url.hash) {
      const [fragment, fragmentQuery = ''] = url.hash.slice(1).split('?');
      const parameters = new URLSearchParams(fragmentQuery);
      incoming.forEach((value, key) => parameters.set(key, value));
      url.hash = fragment + (parameters.size ? `?${parameters}` : '');
    } else {
      incoming.forEach((value, key) => url.searchParams.set(key, value));
    }
    const previewTheme = new URLSearchParams(location.search).get('preview_theme_id');
    if (previewTheme) url.searchParams.set('preview_theme_id', previewTheme);
    return url.href;
  }

  function route() {
    const hash = location.hash || '#/';
    const [path, query = ''] = hash.slice(1).split('?');
    if (path !== '/') {
      const destination = legacyDestination(path, query);
      if (destination) {
        location.replace(destination);
        return;
      }
      if (!path.startsWith('/policies/')) return;
    }
    window.ACTICLEAR_NAV?.closeAll();
    if (dialog?.open) dialog.close();
    const parameters = new URLSearchParams(query);
    if (path === '/') {
      if (currentView !== 'home') restoreHome();
    } else {
      let slug;
      try { slug = decodeURIComponent(path.slice('/policies/'.length)); }
      catch (_) { slug = ''; }
      if (currentView !== `policy:${slug}`) {
        const policy = (config.policies || []).find(item => item.slug === slug);
        main.innerHTML = policy ? policyHTML(policy) : '<section class="empty wrap"><h1>Policy not found</h1><p>Return to the homepage to explore our services.</p><a class="primary" href="#/">Back to home</a></section>';
        document.title = `${policy?.title || 'Policy not found'} — ActiClear`;
        document.body.dataset.route = 'policies';
        currentView = `policy:${slug}`;
      }
    }
    requestAnimationFrame(() => focusSection(parameters.get('section')));
  }

  function releaseVideo() {
    main.querySelectorAll('.ac-home-inline-video video').forEach(video => video.pause());
  }
  function openVideo(trigger) {
    const item = config.video;
    if (!item?.src || !trigger.isConnected) return;
    window.ACTICLEAR_NAV?.closeAll();
    const wrapper = document.createElement('div');
    wrapper.className = 'ac-home-inline-video';
    const video = document.createElement('video');
    video.controls = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.src = item.src;
    if (item.poster) video.poster = item.poster;
    video.tabIndex = 0;
    video.setAttribute('aria-label', item.alt || 'Cooking Oil Solidifier product demonstration');
    const feedback = document.createElement('div');
    feedback.className = 'ac-home-video-feedback';
    const status = document.createElement('span');
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Retry playback';
    retry.hidden = true;
    feedback.append(status, retry);
    wrapper.append(video, feedback);
    const loading = () => { status.textContent = 'Loading video…'; retry.hidden = true; };
    const clear = () => { status.textContent = ''; retry.hidden = true; };
    const failed = () => { status.textContent = 'Unable to play the video. Please try again.'; retry.hidden = false; };
    const play = () => {
      loading();
      if (video.error) video.load();
      // Keep play() in the user's click, including on mobile Safari.
      const promise = video.play();
      promise?.catch(error => {
        if (!video.isConnected || error.name === 'AbortError') return;
        failed();
      });
    };
    video.addEventListener('playing', clear);
    video.addEventListener('pause', () => { if (!video.error && retry.hidden) status.textContent = ''; });
    video.addEventListener('ended', clear);
    video.addEventListener('waiting', () => { if (!video.paused) loading(); });
    video.addEventListener('stalled', () => { if (!video.paused && video.readyState < 3) loading(); });
    video.addEventListener('error', failed);
    retry.addEventListener('click', play);
    if (item.cues?.length) {
      const caption = document.createElement('p');
      caption.className = 'video-step-caption';
      const title = document.createElement('strong');
      const description = document.createElement('span');
      caption.append(title, description);
      wrapper.append(caption);
      const updateCaption = () => {
        const cue = [...item.cues].reverse().find(value => video.currentTime + 0.005 >= value.start) || item.cues[0];
        if (caption.dataset.activeCue === String(cue.start)) return;
        caption.dataset.activeCue = String(cue.start);
        title.textContent = cue.title;
        description.textContent = cue.text;
      };
      ['loadedmetadata', 'timeupdate', 'seeking', 'seeked', 'ended'].forEach(event => video.addEventListener(event, updateCaption));
      updateCaption();
    }
    trigger.replaceWith(wrapper);
    play();
    video.focus({ preventScroll: true });
  }
  window.addEventListener('pagehide', releaseVideo);
  window.addEventListener('hashchange', releaseVideo);
  document.addEventListener('click', event => {
    const element = event.target.closest('button,a');
    if (!element) return;
    if (element.hasAttribute('data-menu')) {
      event.preventDefault();
      window.ACTICLEAR_NAV?.toggleMobile();
    } else if (element.hasAttribute('data-guide-media')) {
      event.preventDefault();
      openVideo(element);
    } else if (element.hasAttribute('data-media-close')) {
      event.preventDefault();
      dialog?.close();
    } else if (element.dataset.nativeurl && element.tagName !== 'A') {
      location.assign(element.dataset.nativeurl);
    } else {
      const href = element.getAttribute('href');
      if (href && href === location.hash && (href === '#/' || href.startsWith('#/?') || href.startsWith('#/policies/'))) {
        event.preventDefault();
        route();
      }
    }
  });
  document.addEventListener('click', event => {
    if (!mobileFooter.matches && event.target.closest('#footer .ac-footer-group summary')) event.preventDefault();
  });
  if (dialog) {
    dialog.addEventListener('close', releaseVideo);
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
    });
  }
  const count = Math.max(0, Math.floor(Number(config.cartCount) || 0));
  const badge = document.getElementById('cart-count');
  if (badge) {
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.hidden = count === 0;
    badge.closest('[data-cart],.cart-icon')?.setAttribute('aria-label', `Shopping bag, ${count} ${count === 1 ? 'item' : 'items'}`);
  }
  mobileFooter.addEventListener('change', syncFooter);
  window.addEventListener('hashchange', route);
  syncFooter();
  route();
})();
